import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../features/auth/context/AuthContext';
import { FaUser, FaLock, FaSignInAlt, FaExclamationTriangle } from 'react-icons/fa';
import Logo from '../components/common/Logo';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, redirectToAppropriateRoute } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Verificar si hay una redirección después del login
  // Prioridad: 1) URL en state de location, 2) URL en sessionStorage, 3) Null (usar default)
  const fromLocation = location.state?.from?.pathname || null;
  const [fromStorage, setFromStorage] = useState(null);
  
  // Recuperar la URL guardada en sessionStorage (si existe)
  useEffect(() => {
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    if (savedRedirect) {
      setFromStorage(savedRedirect);
    }
  }, []);
  
  // Determinar la URL de redirección (prioridad a location.state sobre sessionStorage)
  const from = fromLocation || fromStorage;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor ingresa email y contraseña');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Login user
      const user = await login(email, password);
      console.log('Usuario autenticado:', user);
      
      // Determinar la ruta de redirección basada en el rol del usuario
      let redirectPath;
      const userType = user.get('type') || 'client';
      const isAdmin = userType === 'admin' || user.get('isAdmin') === true;
      
      if (isAdmin) {
        redirectPath = '/admin';
      } else {
        redirectPath = '/user/dashboard';
      }
      
      console.log('Redirigiendo a:', redirectPath);
      navigate(redirectPath);
      
    } catch (error) {
      setError('Credenciales incorrectas. Por favor intenta de nuevo.');
      console.error('Error de inicio de sesión:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <div className="d-inline-block mb-3">
                  <Logo size="medium" showText={false} />
                </div>
                <h2 className="fw-bold">Iniciar Sesión</h2>
                <p className="text-muted">Ingresa tus credenciales para acceder</p>
              </div>
              
              {error && (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                  <FaExclamationTriangle className="me-2" />
                  <div>{error}</div>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Correo Electrónico
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaUser />
                    </span>
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      placeholder="correo@ejemplo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      required
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <label htmlFor="password" className="form-label">
                      Contraseña
                    </label>
                    <Link to="/reset-password" className="small text-decoration-none">
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </div>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaLock />
                    </span>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      required
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2 mb-3"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Iniciando sesión...
                    </>
                  ) : (
                    <>
                      <FaSignInAlt className="me-2" />
                      Iniciar Sesión
                    </>
                  )}
                </button>
                
                <div className="text-center">
                  <p className="mb-0">
                    ¿No tienes una cuenta? <Link to="/signup" className="text-decoration-none">Regístrate</Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
          
          <div className="text-center mt-4">
            <Link to="/" className="text-decoration-none">
              &larr; Volver a la página principal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
